/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2025 (original work) Open Assessment Technologies SA
 */
import moment from 'moment-timezone';

export const MIN_SESSION_DURATION_MINUTES = 30;
/**
 * @param {boolean} hasHour
 * @returns {string}
 */
const getTableDateFormat = hasHour => {
    let dateFormat = 'MMM D, YYYY';
    if (hasHour) {
        dateFormat += ' [at] h:mm a';
    }
    return dateFormat;
};

/**
 * @param {boolean} hasHour
 * @returns {string}
 */
const getInputDateFormat = hasHour => {
    let dateFormat = 'YYYY-MM-DD';
    if (hasHour) {
        dateFormat += ' h:mm';
    }
    return dateFormat;
};

/**
 * @param {Object} params
 * @param {boolean} params.hasHour - has hour in format
 * @param {boolean} params.isForm - is form date
 * @returns {string} - date format
 */
const getDateFormat = params => {
    const { hasHour = true, isForm = false } = params;

    let dateFormat = getTableDateFormat(hasHour);
    if (isForm) {
        dateFormat = getInputDateFormat(hasHour);
    }

    return dateFormat;
};

/**
 * @param {boolean} isUnixMsTime - is unix milliseconds time
 * @returns {string} - unix timestamp
 */
const getTimestampFormat = (isUnixMsTime = true) => {
    let unixTimestamp = 'X';
    if (isUnixMsTime) {
        unixTimestamp = 'x';
    }

    return unixTimestamp;
};

/**
 * @param {number} datetime - datetime in unix (milliseconds) format
 * @param {string} [timezone] - timezone in moment format
 * @param {string} [locale] - optional locale string. Will use browser locale if not provided
 * @returns {Object}
 */
export function toLocaleDateTime(datetime, timezone, locale) {
    // BE sometimes can send a string instead of number, so we have to use type casting here
    const date = moment.tz(moment(Number(datetime)), timezone ?? moment.tz.guess()).local();

    if (locale) {
        moment.locale(locale);
    }

    let dateFormat = getTableDateFormat(false);

    return {
        date: date.format(dateFormat), // formatted date e.g "Jan 1, 2022"
        time: date.format('h:mm a') // formatted time e.g "10:30 am"
    };
}

/**
 * @param {Object} params
 * @param {number} params.epoch - epoch in unix (milliseconds) format
 * @param {string} [params.timezone] - timezone in moment format
 * @param {boolean} params.isForm - if true produces output as YYYY-MM-DD instead of "Jan 1, 2022"
 * @param {boolean} params.hasHour - if true includes the time portion
 * @param {boolean} params.isUnixMsTime
 * @returns {string}
 * @example formatEpoch({ epoch: 1729173780000, timezone: 'Europe/Paris', locale: 'en-US' }) // "Oct 17, 2024 at 4:03 pm"
 */
export function formatEpoch(params) {
    const { epoch, timezone, isUnixMsTime, ...rest } = params;

    const dateFormat = getDateFormat(rest);
    const unixTimestamp = getTimestampFormat(isUnixMsTime);

    if (timezone) {
        return moment.tz(epoch, unixTimestamp, timezone).format(dateFormat);
    }

    return moment(epoch).format(dateFormat);
}

/**
 * @param {Object} params
 * @param {number} params.epoch - epoch in unix (milliseconds) format
 * @param {string} params.timezone - timezone in moment format
 * @param {boolean} params.isForm
 * @param {boolean} params.hasHour
 * @param {boolean} params.isUnixMsTime
 * @returns {Date}
 */
export function createDateFromEpoch(params) {
    return new Date(formatEpoch(params));
}

/**
 * @param {Object} params
 * @param {number|any} params.date - date in unix (milliseconds) format
 * @param {string} params.timezone - timezone in moment format
 * @param {boolean} params.isForm
 * @param {boolean} params.hasHour
 * @returns {string|any}
 */
export function getValueOfDate(params) {
    const { date, timezone, ...rest } = params;
    if (!date) {
        return date;
    }

    const dateFormat = getDateFormat(rest);

    return moment.tz(date, dateFormat, timezone).valueOf();
}

/**
 * @returns {[string]}
 */
export function getTimezoneNamesList() {
    const countryCodes = moment.tz.countries();
    const zonesForCountry = countryCodes.map(code => moment.tz.zonesForCountry(code));
    const zones = [...new Set(zonesForCountry.flat())].sort();
    zones.unshift('UTC');
    return zones;
}

/**
 * @returns {string}
 */
export function getLocalTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function addExtraTime(count = 0, duration = 'hours', when) {
    return moment(when).add(count, duration).valueOf();
}

export function removeExtraTime(count = 0, duration = 'hours', when) {
    return moment(when).subtract(count, duration).valueOf();
}

export function getHourStart(time) {
    return moment(time).startOf('hour').valueOf();
}

export function timestampToFormattedDateString(timestamp, timezone, format = 'YYYY-MM-DDTHH:mm:ssZ') {
    if (!timestamp || !moment(timestamp).isValid()) {
        return '';
    }

    return moment.tz(timestamp, timezone).format(format);
}

export function convertDateStringWithTimeZoneToDate(dateString, format, timezone) {
    const momentWithUserTimezone = new moment.tz(dateString, format, timezone);
    const newMoment = moment([
        momentWithUserTimezone.year(),
        momentWithUserTimezone.month(),
        momentWithUserTimezone.date(),
        momentWithUserTimezone.hour(),
        momentWithUserTimezone.minute(),
        0,
        0
    ]);
    return newMoment.toDate();
}

/**
 * Adjusts a date string to a target timezone while preserving the same local time.
 *
 * @param {string} dateString - The date string to adjust.
 * @param {string} timezone - The target timezone.
 * @returns {string} - The adjusted date string.
 */
export function adjustToTimezonePreservingLocalTime(dateString, timezone) {
    if (!dateString) {
        return dateString;
    }
    const localMoment = moment.parseZone(dateString);

    return localMoment.clone().tz(timezone, true).format();
}

/**
 *
 * @param {object} ranges
 * @param {number} [ranges.minDate] - date in unix (milliseconds) format
 * @param {number} [ranges.maxDate] - date in unix (milliseconds) format
 * @param {string} timezone
 * @returns {object} - input ranges aligned to new timezone
 */
export function alignDateRangesWithTimezone(ranges, timezone) {
    if (!timezone) {
        return ranges;
    }

    const minDate = ranges?.minDate ? moment.tz(ranges.minDate, timezone).format() : false;
    const maxDate = ranges?.maxDate ? moment.tz(ranges.maxDate, timezone).format() : false;
    return {
        minDate,
        maxDate
    };
}

/**
 * Checks if the current date is between the provided start and end dates.
 * @param {number} startDate
 * @param {number} endDate
 * @returns {boolean}
 */
export function isCurrentTimeInRange(startDate, endDate) {
    const now = Date.now();
    return (!startDate || now >= startDate) && (!endDate || now <= endDate);
}

/**
 * Check if the date is in the past and return the current time if it is
 * @param {Date} date
 * @returns {Date} date or current time
 */
// method to check if the date is in the past and return the current time if it is
export const getCurrentIfInThePast = date => (moment(date).isBefore(moment()) ? addExtraTime() : date);
